import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, Camera, CheckCircle2, Loader2, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ensurePlateOcrReady, recognizePlateFromCapture } from '@/services/plateOcrService'
import { normalizeLicensePlate } from '@/utils/licensePlate'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'

/** Must match the on-screen guide box and capture crop. */
const PLATE_REGION = {
  widthRatio: 0.96,
  heightRatio: 0.44,
}

function waitForVideoFrames(video, timeoutMs = 6000) {
  return new Promise((resolve) => {
    if (video.videoWidth > 0 && video.readyState >= 2) {
      resolve(true)
      return
    }
    const started = Date.now()
    const tick = () => {
      if (video.videoWidth > 0 && video.readyState >= 2) {
        resolve(true)
        return
      }
      if (Date.now() - started > timeoutMs) {
        resolve(false)
        return
      }
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  })
}

function capturePlateStill(video) {
  const vw = video.videoWidth
  const vh = video.videoHeight
  if (!vw || !vh) throw new Error('Camera not ready')

  const boxW = vw * PLATE_REGION.widthRatio
  const boxH = vh * PLATE_REGION.heightRatio
  const sx = Math.max(0, (vw - boxW) / 2)
  const sy = Math.max(0, (vh - boxH) / 2)

  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1280, Math.floor(boxW))
  canvas.height = Math.max(200, Math.floor(boxH))
  const ctx = canvas.getContext('2d')
  ctx.drawImage(video, sx, sy, boxW, boxH, 0, 0, canvas.width, canvas.height)
  return canvas
}

function isBenignVideoError(error) {
  const name = error?.name || ''
  const msg = String(error?.message || '')
  return (
    name === 'AbortError' ||
    /interrupted|aborted|removed from the document|new load request/i.test(msg)
  )
}

function attachStreamAndPlay(video, stream, sessionId, isActive) {
  return new Promise((resolve, reject) => {
    if (!isActive(sessionId)) {
      resolve(false)
      return
    }

    let settled = false
    const finish = (ok) => {
      if (settled) return
      settled = true
      video.removeEventListener('loadedmetadata', onReady)
      resolve(ok)
    }

    const onReady = () => {
      if (!isActive(sessionId)) {
        finish(false)
        return
      }
      video
        .play()
        .then(() => finish(isActive(sessionId)))
        .catch((error) => {
          if (isBenignVideoError(error)) {
            finish(false)
            return
          }
          reject(error)
        })
    }

    video.addEventListener('loadedmetadata', onReady)
    video.srcObject = stream
    if (video.readyState >= 1) onReady()
  })
}

function ReadyBanner({ cameraReady, ocrReady, ocrEngine, ocrLoading, starting }) {
  const fullyReady = cameraReady && ocrReady

  let message = 'Preparing scanner…'
  if (starting && !cameraReady) message = 'Starting camera…'
  else if (cameraReady && ocrLoading) {
    message = 'Loading OCR engine (first time may take 20–30 seconds)…'
  } else if (cameraReady && !ocrReady) message = 'Finishing OCR setup…'
  else if (fullyReady) message = 'Ready — align plate in box and tap Capture & read'

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm',
        fullyReady
          ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-950 dark:text-emerald-100'
          : 'border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100'
      )}
      role="status"
      aria-live="polite"
    >
      {fullyReady ? (
        <CheckCircle2 className="size-5 shrink-0 text-emerald-600" aria-hidden />
      ) : (
        <Loader2 className="size-5 shrink-0 animate-spin" aria-hidden />
      )}
      <div className="min-w-0">
        <p className="font-semibold">{fullyReady ? 'Ready to scan' : 'Getting ready…'}</p>
        <p className="text-xs opacity-90 mt-0.5">{message}</p>
        {fullyReady && ocrEngine && (
          <p className="text-[11px] opacity-75 mt-1">
            Camera live · OCR: {ocrEngine === 'paddleocr' ? 'PaddleOCR' : 'Tesseract'}
          </p>
        )}
        {!fullyReady && (
          <ul className="mt-2 space-y-0.5 text-[11px] opacity-80">
            <li>{cameraReady ? '✓ Camera' : '○ Camera'}</li>
            <li>{ocrReady ? '✓ OCR engine' : '○ OCR engine'}</li>
          </ul>
        )}
      </div>
    </div>
  )
}

export default function PlateScanner({
  open,
  onClose,
  onPlateDetected,
  onOcrFailed,
  onScanCustomerQr,
  onManualEntry,
}) {
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const captureCanvasRef = useRef(null)
  const cameraSessionRef = useRef(0)

  const [mode, setMode] = useState('live')
  const [starting, setStarting] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [ocrReady, setOcrReady] = useState(false)
  const [ocrLoading, setOcrLoading] = useState(false)
  const [ocrEngine, setOcrEngine] = useState('')
  const [status, setStatus] = useState('')
  const [capturePreview, setCapturePreview] = useState('')
  const [detectedPlate, setDetectedPlate] = useState('')
  const [manualPlate, setManualPlate] = useState('')
  const [ocrCandidates, setOcrCandidates] = useState([])
  const [ocrRawHint, setOcrRawHint] = useState('')

  const scannerReady = cameraReady && ocrReady

  const isActiveSession = useCallback((sessionId) => cameraSessionRef.current === sessionId, [])

  const stopCamera = useCallback(() => {
    cameraSessionRef.current += 1
    setCameraReady(false)
    const video = videoRef.current
    if (video) {
      try {
        video.pause()
      } catch (_) {
        /* ignore */
      }
      video.srcObject = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
  }, [])

  const clearCapture = useCallback(() => {
    captureCanvasRef.current = null
    if (capturePreview) URL.revokeObjectURL(capturePreview)
    setCapturePreview('')
    setDetectedPlate('')
    setOcrCandidates([])
    setOcrRawHint('')
  }, [capturePreview])

  const loadOcrEngine = useCallback(async () => {
    setOcrLoading(true)
    setOcrReady(false)
    try {
      const { engine } = await ensurePlateOcrReady()
      setOcrEngine(engine)
      setOcrReady(true)
    } catch (e) {
      console.error(e)
      setOcrReady(false)
      toast.error('OCR engine failed to load. Try manual entry or QR scan.')
    } finally {
      setOcrLoading(false)
    }
  }, [])

  const startCamera = useCallback(async () => {
    stopCamera()
    clearCapture()
    setMode('live')
    const sessionId = cameraSessionRef.current
    setStarting(true)
    setCameraReady(false)
    setStatus('')

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error('Camera not supported on this device.')
        onOcrFailed?.()
        return
      }

      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        })
      } catch (_) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      }

      if (!isActiveSession(sessionId)) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      streamRef.current = stream
      const video = videoRef.current
      if (!video) return

      await attachStreamAndPlay(video, stream, sessionId, isActiveSession)
      const framesOk = await waitForVideoFrames(video)
      if (!isActiveSession(sessionId)) return

      if (framesOk || video.videoWidth > 0) {
        setCameraReady(true)
      }
    } catch (e) {
      if (isBenignVideoError(e) || !isActiveSession(sessionId)) return
      console.error(e)
      toast.error('Could not access the camera.')
      onOcrFailed?.()
    } finally {
      if (isActiveSession(sessionId)) setStarting(false)
    }
  }, [clearCapture, isActiveSession, onOcrFailed, stopCamera])

  useEffect(() => {
    if (!open) {
      stopCamera()
      clearCapture()
      setMode('live')
      setStatus('')
      setProcessing(false)
      setManualPlate('')
      setOcrCandidates([])
      setOcrRawHint('')
      setStarting(false)
      setOcrReady(false)
      setOcrLoading(false)
      setOcrEngine('')
      return
    }

    const timer = window.setTimeout(() => {
      void loadOcrEngine()
      void startCamera()
    }, 80)

    return () => {
      window.clearTimeout(timer)
      stopCamera()
      clearCapture()
    }
  }, [open, startCamera, stopCamera, clearCapture, loadOcrEngine])

  async function handleCaptureAndRead() {
    const video = videoRef.current
    if (!video || processing || mode !== 'live' || !scannerReady) return

    setProcessing(true)
    setStatus('Capturing…')

    try {
      const ready = await waitForVideoFrames(video, 2000)
      if (!ready) {
        toast.error('Camera not ready — wait for the green Ready banner')
        return
      }

      const canvas = capturePlateStill(video)
      captureCanvasRef.current = canvas
      stopCamera()

      const previewUrl = canvas.toDataURL('image/jpeg', 0.92)
      setCapturePreview(previewUrl)
      setMode('captured')
      setStatus('Reading image — may take a few seconds…')

      const result = await recognizePlateFromCapture(canvas)

      const uniqueCandidates = Array.from(
        new Set(
          [result.plate, ...(result.candidates || [])]
            .map((c) => normalizeLicensePlate(c))
            .filter((c) => c.length >= 4)
        )
      )

      setOcrCandidates(uniqueCandidates)
      setOcrRawHint(result.rawText || '')

      const plate = uniqueCandidates[0] || null
      if (!plate) {
        setStatus("Couldn't read the plate from this capture")
        toast.error('Could not read the plate. Tap a suggestion below or enter manually.')
        return
      }

      const norm = normalizeLicensePlate(plate)

      if (result.needsConfirmation) {
        setStatus('Tap the correct registration below')
        return
      }

      setDetectedPlate(norm)
      setStatus(`${norm} detected (${result.confidence}% · ${result.engine || 'ocr'})`)
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try {
          navigator.vibrate(80)
        } catch (_) {
          /* ignore */
        }
      }
      onPlateDetected?.(norm)
    } catch (e) {
      console.error(e)
      setStatus("Couldn't read the captured image")
      toast.error('OCR failed. Retake or enter the plate manually.')
    } finally {
      setProcessing(false)
    }
  }

  function handleRetake() {
    clearCapture()
    setMode('live')
    setStatus('')
    void startCamera()
  }

  function confirmCandidate(plate) {
    const norm = normalizeLicensePlate(plate)
    if (norm.length < 4) return
    setDetectedPlate(norm)
    setStatus(`${norm} selected`)
    onPlateDetected?.(norm)
  }

  function submitManual() {
    const norm = normalizeLicensePlate(manualPlate)
    if (norm.length < 4) {
      toast.error('Enter a valid registration (at least 4 characters)')
      return
    }
    onManualEntry?.(norm)
  }

  if (!open) return null

  if (mode === 'manual') {
    return (
      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        <p className="text-sm font-medium text-foreground">Enter plate manually</p>
        <div className="space-y-2">
          <Label htmlFor="manual-plate">Registration</Label>
          <Input
            id="manual-plate"
            value={manualPlate}
            onChange={(e) => setManualPlate(e.target.value.toUpperCase())}
            placeholder="ABC123GP"
            className="font-mono uppercase"
            autoFocus
          />
        </div>
        <div className="flex flex-col gap-2">
          <Button type="button" onClick={submitManual}>
            Search registration
          </Button>
          <Button type="button" variant="secondary" onClick={() => setMode(capturePreview ? 'captured' : 'live')}>
            Back
          </Button>
          <Button type="button" variant="ghost" onClick={onScanCustomerQr}>
            Scan Customer QR
          </Button>
        </div>
      </div>
    )
  }

  if (mode === 'captured') {
    return (
      <div className="space-y-3">
        <div className="relative overflow-hidden rounded-xl border bg-black">
          {capturePreview ? (
            <img
              src={capturePreview}
              alt="Captured licence plate"
              className="min-h-[220px] w-full object-contain bg-black sm:min-h-[280px]"
            />
          ) : null}
          <div className="absolute inset-x-0 bottom-0 bg-black/75 px-3 py-2">
            <p className="text-center text-sm font-medium text-white">
              {status || (detectedPlate ? `${detectedPlate} detected` : 'Captured image')}
            </p>
          </div>
        </div>

        {processing && (
          <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            Reading captured image…
          </p>
        )}

        {!processing && ocrCandidates.length > 0 && (
          <div className="space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
            <p className="text-sm font-medium">
              {detectedPlate ? 'Not right? Pick another reading:' : 'Did we read one of these?'}
            </p>
            <div className="flex flex-wrap gap-2">
              {ocrCandidates.slice(0, 4).map((candidate) => (
                <Button
                  key={candidate}
                  type="button"
                  variant={candidate === detectedPlate ? 'default' : 'secondary'}
                  className="font-mono uppercase"
                  onClick={() => confirmCandidate(candidate)}
                >
                  {candidate}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button type="button" variant="secondary" onClick={handleRetake} disabled={processing}>
            <RotateCcw className="size-4" />
            Retake photo
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setMode('manual')}>
            Enter plate manually
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onScanCustomerQr}>
            Scan Customer QR instead
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="size-4" />
            Back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <ReadyBanner
        cameraReady={cameraReady}
        ocrReady={ocrReady}
        ocrEngine={ocrEngine}
        ocrLoading={ocrLoading}
        starting={starting}
      />

      <div className="relative overflow-hidden rounded-xl border bg-black">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="min-h-[360px] w-full object-cover sm:min-h-[420px]"
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/70 to-transparent px-3 py-2.5">
          <p className="text-center text-sm font-medium text-white">
            {scannerReady
              ? 'Fill the box with the licence plate'
              : status || 'Waiting for camera and OCR…'}
          </p>
        </div>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-2">
          <div
            className="w-[96%] max-w-lg rounded-xl border-[3px] border-emerald-400/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
            style={{ height: `${PLATE_REGION.heightRatio * 100}%` }}
          />
        </div>
        {scannerReady && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-emerald-600/90 px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-white">
            Ready
          </div>
        )}
      </div>

      <Button
        type="button"
        className={cn('h-14 w-full text-base', scannerReady && 'shadow-md')}
        disabled={starting || processing || !scannerReady}
        onClick={() => void handleCaptureAndRead()}
      >
        {processing ? (
          <>
            <Loader2 className="size-5 animate-spin" />
            Processing…
          </>
        ) : scannerReady ? (
          <>
            <Camera className="size-5" />
            Capture &amp; read
          </>
        ) : (
          <>
            <Loader2 className="size-5 animate-spin" />
            {ocrLoading ? 'Loading OCR…' : starting ? 'Starting camera…' : 'Not ready yet'}
          </>
        )}
      </Button>

      <div className="flex flex-col gap-2">
        <Button type="button" variant="secondary" onClick={onClose}>
          <ArrowLeft className="size-4" />
          Back
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            stopCamera()
            setMode('manual')
          }}
        >
          Enter plate manually
        </Button>
      </div>
    </div>
  )
}
